import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, MessageCircle } from 'lucide-react';
import { companyInfo } from '../mockData';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you soon.",
    });

    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: ''
    });
  };

  return (
    <div>
      {/* Creative Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-pink-700 py-40 mt-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, white 2px, transparent 2px), radial-gradient(circle at 80% 80%, white 2px, transparent 2px)',
          backgroundSize: '60px 60px'
        }}></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-pink-400/20 rounded-full blur-3xl animate-float" style={{animationDelay: '1s'}}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
            <MessageCircle size={20} className="text-purple-200" />
            <span className="text-white font-bold text-sm">Let's Talk</span>
          </div>
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-none">
            <span className="block">Get In</span>
            <span className="block text-purple-200">Touch</span>
          </h1>
          <p className="text-2xl text-purple-100 max-w-3xl mx-auto leading-relaxed">
            Ready to scale your business? Let's discuss how we can help you grow.
          </p>
        </div>
      </div>

      {/* Contact Section - Asymmetric Creative Layout */}
      <section className="py-32 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-100 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-100 rounded-full opacity-20 blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Information - Creative Cards (2 columns) */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                  <span className="block">Contact</span>
                  <span className="gradient-text bg-gradient-to-r from-purple-600 to-pink-600" style={{
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>Information</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Have a question or ready to get started? Reach out to us and we'll respond as soon as possible.
                </p>
              </div>

              {/* Creative Contact Cards */}
              <div className="space-y-6">
                <div className="group bg-gradient-to-br from-purple-600 to-purple-700 rounded-3xl p-8 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                      <MapPin size={28} className="text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="font-black text-xl mb-2">Address</h3>
                      <p className="text-purple-100 leading-relaxed">{companyInfo.address}</p>
                    </div>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-pink-600 to-pink-700 rounded-3xl p-8 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Phone size={28} className="text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="font-black text-xl mb-2">Phone</h3>
                      <a href={`tel:${companyInfo.phone}`} className="text-pink-100 hover:text-white transition-colors text-lg font-bold">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="group bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl p-8 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Mail size={28} className="text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="font-black text-xl mb-2">Email</h3>
                      <a href={`mailto:${companyInfo.email}`} className="text-purple-100 hover:text-white transition-colors break-all font-bold">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours - Creative Card */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border-2 border-purple-100 shadow-lg">
                <div className="flex items-center space-x-3 mb-6">
                  <Clock size={24} className="text-purple-600" />
                  <h3 className="font-black text-gray-900 text-xl">Business Hours</h3>
                </div>
                <div className="space-y-3 text-gray-700">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="font-bold">Monday - Friday:</span>
                    <span className="font-black text-purple-600">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="font-bold">Saturday:</span>
                    <span className="font-black text-purple-600">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-bold">Sunday:</span>
                    <span className="font-black text-gray-400">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form - Creative Design (3 columns) */}
            <div className="lg:col-span-3">
              <div className="bg-gradient-to-br from-white to-purple-50 rounded-3xl border-2 border-purple-200 p-10 shadow-2xl">
                <h2 className="text-3xl font-black text-gray-900 mb-2">
                  Send Us a Message
                </h2>
                <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you within 24 hours</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-black text-gray-900 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all duration-200 font-medium"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-black text-gray-900 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all duration-200 font-medium"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-black text-gray-900 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all duration-200 font-medium"
                        placeholder="+1 234 567 8900"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-black text-gray-900 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all duration-200 font-medium"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-black text-gray-900 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all duration-200 resize-none font-medium"
                      placeholder="Tell us about your project..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="group w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-10 py-5 rounded-2xl font-black text-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3 relative overflow-hidden"
                  >
                    <span className="relative z-10">Send Message</span>
                    <Send size={24} className="relative z-10 group-hover:translate-x-2 transition-transform" />
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;